/* eslint-disable max-lines-per-function, max-statements */
import { AddHomeVideoReviewContext } from "contexts/MightyTool";
import { DEFAULT_HOME_VIDEO_REVIEW } from "contexts/Reviews";
import useAddEditHomeVideoReview from "hooks/useAddEditHomeVideoReview";
import useCreateReviews from "hooks/useCreateReviews";
import useIsAddHomeVideoReviewEnabled from "hooks/useIsAddHomeVideoReviewEnabled";
import useIsImdbIdValid from "hooks/useIsImdbIdValid";
import usePreviews from "hooks/usePreviews";
import useReviews from "hooks/useReviews";
import useTMDBFindByImdbId from "hooks/useTMDBFindById";
import useTMDBSearch from "hooks/useTMDBSearch";
import useTMDBSuggestions from "hooks/useTMDBSuggestions";
import equals from "ramda/src/equals";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useIsHomeVideoReviewReadyToBeCreated } from "./helpers";
/**
 * useAddHomeVideoReview
 *
 * @description Hook to add a Home Video Review
 * @param {UseAddHomeVideoReviewProps} props - Props
 * @param {UseAddHomeVideoReviewProps} props.IMDbId - IMDbId
 * @param {UseAddHomeVideoReviewProps} props.isAddHomeVideoReviewEnabled - Is Add Home Video Review enabled
 * @param {UseAddHomeVideoReviewProps} props.isImdbIdValid - Is IMDbId valid
 * @param {UseAddHomeVideoReviewProps} props.loading - Is loading
 * @param {UseAddHomeVideoReviewProps} props.name - Name
 * @param {UseAddHomeVideoReviewProps} props.onAddHomeVideoReview - On Add Home Video Review
 * @param {UseAddHomeVideoReviewProps} props.onClearAll - On Clear All
 * @returns {UseAddHomeVideoReviewReturn}
 *
 */
const useAddHomeVideoReview = (props) => {
    const { resetImages } = usePreviews();
    const { addHomeVideoReview, called, data, loading: addHomeVideoReviewLoading, reset } = useContext(AddHomeVideoReviewContext);
    // Review State
    const { clearAll, resetSelectedReviews, setTmdbSelectedImdbId, setTmdbSelectedId, state: { avForums, avNirvana, avsForum, bluRay, bluRayAuthority, digitalBits, doBlu, hidefDigest, highDefDiscNews, homeTheaterForum, homeVideoReview, theaterByte, tmdbSelectedImdbId, tmdbSelectedId, whySoBlu } } = useReviews();
    // Create Reviews State
    const { areReviewsCalled, areReviewsLoading, onCreateReviews, resetReviews } = useCreateReviews();
    // Clear All
    const { onClearAll } = useAddEditHomeVideoReview();
    // Effective IMDb ID: TMDB selection supersedes critic review selection
    const effectiveImdbId = useMemo(() => tmdbSelectedImdbId ?? homeVideoReview.IMDbId, [homeVideoReview.IMDbId, tmdbSelectedImdbId]);
    // TMDB Search State
    const [tmdbSearchQuery, setTmdbSearchQuery] = useState("");
    const { loading: tmdbLoading, data: tmdbData } = useTMDBSearch({
        query: tmdbSearchQuery,
        type: "both"
    });
    // TMDB Find by IMDb ID (auto-triggered when critic review sets IMDbId)
    const { data: tmdbFindByData } = useTMDBFindByImdbId({
        imdbId: homeVideoReview.IMDbId
    });
    // Unified TMDB suggestions: combines findBy-IMDb and user search results
    const tmdbSuggestions = useTMDBSuggestions({
        findByData: tmdbFindByData,
        searchData: tmdbData
    });
    const handleTmdbSearch = useCallback((query) => {
        setTmdbSearchQuery(query);
    }, []);
    const handleTmdbSelect = useCallback((tmdbId) => {
        setTmdbSelectedId(tmdbId);
        const selectedItem = tmdbSuggestions.find((item) => item.id === tmdbId);
        if (selectedItem?.externalIds?.imdbId) {
            setTmdbSelectedImdbId(selectedItem.externalIds.imdbId);
        }
    }, [setTmdbSelectedId, setTmdbSelectedImdbId, tmdbSuggestions]);
    // Auto-select TMDB result when findTMDB returns exactly 1 item
    // and no TMDB selection exists yet (i.e., IMDb ID came from critic review)
    useEffect(() => {
        if (tmdbFindByData?.length === 1 && !tmdbSelectedId) {
            const [item] = tmdbFindByData;
            setTmdbSelectedId(item.id.toString());
            if (item.externalIds?.imdbId) {
                setTmdbSelectedImdbId(item.externalIds.imdbId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tmdbFindByData]);
    // Are all Home Video Review properties ready?
    const isHomeVideoReviewReadyToBeCreated = useIsHomeVideoReviewReadyToBeCreated({
        avForums: avForums.selected,
        avNirvana: avNirvana.selected,
        avsForum: avsForum.selected,
        bluRay: bluRay.selected,
        bluRayAuthority: bluRayAuthority.selected,
        digitalBits: digitalBits.selected,
        doBlu: doBlu.selected,
        effectiveImdbId,
        hidefDigest: hidefDigest.selected,
        highDefDiscNews: highDefDiscNews.selected,
        homeTheaterForum: homeTheaterForum.selected,
        homeVideoReview,
        theaterByte: theaterByte.selected,
        whySoBlu: whySoBlu.selected
    });
    // If reviews are called and Home Video Review is ready to be created and
    // create hasn't been called then create a Mighty Chroma
    useEffect(() => {
        if (props?.isReadOnly) {
            return;
        }
        if (areReviewsCalled) {
            if (isHomeVideoReviewReadyToBeCreated && !called) {
                addHomeVideoReview({
                    variables: {
                        homeVideoReview: {
                            ...homeVideoReview,
                            IMDbId: homeVideoReview.isAddHomeVideo
                                ? effectiveImdbId
                                : undefined,
                            isPublished: false
                        }
                    }
                });
            }
        }
    }, [
        addHomeVideoReview,
        areReviewsCalled,
        called,
        effectiveImdbId,
        isHomeVideoReviewReadyToBeCreated,
        homeVideoReview,
        props?.isReadOnly
    ]);
    // If Home Video Review has been created then reset reviews
    useEffect(() => {
        if (props?.isReadOnly) {
            return;
        }
        if (data) {
            if (!equals(homeVideoReview, DEFAULT_HOME_VIDEO_REVIEW)) {
                resetSelectedReviews();
                reset();
            }
            // Reset images
            resetImages();
            // Reset reviews
            resetReviews();
            // Clear all reviews
            clearAll();
        }
    }, [
        clearAll,
        data,
        homeVideoReview,
        props?.isReadOnly,
        reset,
        resetImages,
        resetReviews,
        resetSelectedReviews
    ]);
    // Are we creating reviews or a Mighty Chroma?
    const loading = useMemo(() => addHomeVideoReviewLoading || areReviewsLoading, [addHomeVideoReviewLoading, areReviewsLoading]);
    /**
     * isImdbIdValid
     *
     * @description Check if IMDbId is valid
     */
    const isImdbIdValid = useIsImdbIdValid(effectiveImdbId);
    // Are all Mighty Chroma properties ready for the create button to be enabled?
    const isAddHomeVideoReviewEnabled = useIsAddHomeVideoReviewEnabled({
        homeVideoReview,
        isImdbIdValid,
        loading,
        services: {
            avForums: avForums.selected,
            avNirvana: avNirvana.selected,
            avsForum: avsForum.selected,
            bluRay: bluRay.selected,
            bluRayAuthority: bluRayAuthority.selected,
            digitalBits: digitalBits.selected,
            doBlu: doBlu.selected,
            hidefDigest: hidefDigest.selected,
            highDefDiscNews: highDefDiscNews.selected,
            homeTheaterForum: homeTheaterForum.selected,
            theaterByte: theaterByte.selected,
            whySoBlu: whySoBlu.selected
        }
    });
    return {
        IMDbId: effectiveImdbId,
        hasSelectedIcon: Boolean(tmdbSelectedId),
        isAddHomeVideoReviewEnabled,
        isImdbIdValid,
        loading,
        name: homeVideoReview.name,
        onAddHomeVideoReview: onCreateReviews,
        onClearAll,
        onTmdbSearch: handleTmdbSearch,
        onTmdbSelect: handleTmdbSelect,
        tmdbSelectedId,
        tmdbSuggestions,
        tmdbLoading
    };
};
export default useAddHomeVideoReview;
